import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, US_STATES, CARE_TYPES } from "@/lib/api";
import { HeartHandshake } from "lucide-react";

export default function QuickHelpDialog({ children, sourceTag = "landing_hero" }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    preferred_area: "",
    care_priority: "",
    loved_one_name: "",
    timeline: "1_3_months",
    message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) return toast.error("Please share your name.");
    if (!form.contact_phone.trim() && !form.contact_email.trim()) return toast.error("Please add a phone number OR email so we can reach you.");
    setSubmitting(true);
    try {
      await api.post("/placement-requests", { ...form, referral_source: sourceTag });
      toast.success("Got it — a Providence advisor will call you within 1 business day.");
      setOpen(false);
      setForm({ contact_name: "", contact_phone: "", contact_email: "", preferred_area: "", care_priority: "", loved_one_name: "", timeline: "1_3_months", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please call (586) 306-8449 directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent data-testid="quick-help-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <HeartHandshake className="h-5 w-5" />
          </div>
          <DialogTitle className="text-center font-serif text-2xl text-forest">Get free help finding care</DialogTitle>
          <DialogDescription className="text-center">
            Tell us a bit about who you're caring for. A Providence advisor will call within 1 business day —
            <strong className="text-forest"> no cost, no obligation, no lead-mill.</strong>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3.5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Your name *</label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g. Sarah Johnson" data-testid="qh-name" className="mt-1 h-11" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Phone</label>
              <Input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="(555) 123-4567" data-testid="qh-phone" className="mt-1 h-11" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Email (or phone above)</label>
            <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="you@example.com" data-testid="qh-email" className="mt-1 h-11" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">State</label>
              <select value={form.preferred_area} onChange={(e) => setForm({ ...form, preferred_area: e.target.value })} data-testid="qh-state" className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm">
                <option value="">Select…</option>
                {US_STATES.map((s) => (<option key={s.code} value={s.code}>{s.name}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Care needed</label>
              <select value={form.care_priority} onChange={(e) => setForm({ ...form, care_priority: e.target.value })} data-testid="qh-care" className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm">
                <option value="">Select…</option>
                {CARE_TYPES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                <option value="memory_care">Memory Care</option>
                <option value="autism">Autism / IDD support</option>
                <option value="hospice_certified">Hospice / End-of-life care</option>
                <option value="not_sure">Not sure — need guidance</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Anything we should know? (optional)</label>
            <Textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="e.g. Mom has early-stage dementia, needs weekday drop-in" data-testid="qh-message" className="mt-1" />
          </div>
          <DialogFooter className="mt-1">
            <Button type="submit" disabled={submitting} data-testid="qh-submit" className="w-full rounded-full bg-primary py-6 text-primary-foreground">
              {submitting ? "Sending…" : "Get free help now"}
            </Button>
          </DialogFooter>
          <p className="text-center text-xs text-muted-foreground">
            Prefer to call? <a href="tel:+15863068449" className="font-medium text-primary hover:underline">(586) 306-8449</a> — always available, 24/7.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
